/**
 * Types and default rows for settlement notification tracking (Phase 5).
 * Persisted under settlement frame data (per SFrame route id).
 */

export type NotificationStatus = 'pending' | 'sent' | 'acknowledged' | 'completed';

export interface TrackedNotification {
  id: string;
  name: string;
  category?: string;
  status: NotificationStatus;
  /** User-set follow-up / proof deadline (ISO yyyy-mm-dd) */
  deadlineDate?: string;
  /** Show in-app reminder when deadline is within this many days */
  reminderDaysBefore: number;
  /** Notes (fax/certified mail/portal confirmation or other) */
  notes?: string;
  /** Short guidance (not legal advice) */
  guidanceText?: string;
  /** Stable key if row was created from planner import */
  plannerRef?: string;
}

export type NotificationPreset =
  | 'official'
  | 'government'
  | 'financial'
  | 'accounts'
  | 'employer'
  | 'creditor'
  | 'subscriptions';

export interface NotificationTrackingPayload {
  version: 1;
  institutions: TrackedNotification[];
  /** ISO date; month shown first in calendar */
  calendarFocusDate?: string;
}

export const NOTIFICATION_TRACKING_VERSION = 1 as const;

function row(
  partial: Omit<TrackedNotification, 'id' | 'status' | 'reminderDaysBefore'> & {
    status?: NotificationStatus;
    reminderDaysBefore?: number;
    proofNotes?: string;
  }
): Omit<TrackedNotification, 'id'> {
  return {
    status: partial.status ?? 'pending',
    reminderDaysBefore: partial.reminderDaysBefore ?? 7,
    name: partial.name,
    category: partial.category,
    deadlineDate: partial.deadlineDate,
    // support older proofNotes field while preferring 'notes'
    notes: partial.notes ?? partial.proofNotes,
    guidanceText: partial.guidanceText,
    plannerRef: partial.plannerRef,
  };
}

export function defaultInstitutionsForPreset(preset: NotificationPreset): Omit<TrackedNotification, 'id'>[] {
  switch (preset) {
    case 'official':
    case 'government':
      return [
        row({
          name: 'Social Security Administration',
          category: 'Federal',
          guidanceText:
            'Report the death promptly; SSA typically expects notice in a timely manner (often discussed as within the month of death—confirm current SSA guidance).',
        }),
        row({
          name: 'Department of Veterans Affairs (VA)',
          category: 'Federal',
          guidanceText:
            'Survivor benefits and claims can take multiple steps; many families plan on roughly 2–3 months or longer for VA processing.',
        }),
        row({
          name: 'Medicare / Medicaid (CMS / state Medicaid)',
          category: 'Federal / State',
          guidanceText: 'Stop inappropriate billing and coordinate any survivor coverage transitions.',
        }),
        row({
          name: 'State agencies (DMV, voter registration, tax)',
          category: 'State',
          guidanceText: 'Driver license cancellation and voter rolls reduce identity theft risk.',
        }),
        row({
          name: 'Publication / legal notice to creditors',
          category: 'Publication',
          guidanceText: 'State probate rules set publication windows and proof deadlines for unknown creditors.',
        }),
        row({
          name: 'Known creditors',
          category: 'Creditors',
          guidanceText: 'Direct notice to creditors starts the claims window and helps document estate administration.',
        }),
      ];
    case 'financial':
      return [
        row({
          name: 'Primary bank / credit union (depository accounts)',
          category: 'Bank — freeze / retitle',
          guidanceText:
            'Notify and request account holds per institution policy; joint and POD rules vary by state and contract.',
        }),
        row({
          name: 'Brokerage / investment custodian',
          category: 'Investments',
          guidanceText: 'Retitle or transfer per beneficiary designations and estate documents.',
        }),
        row({
          name: '401(k) / 403(b) / IRA administrator',
          category: 'Retirement',
          guidanceText: 'Beneficiary payouts have tax and timing choices; coordinate with tax advisor.',
        }),
        row({
          name: 'Life insurance carrier(s)',
          category: 'Life insurance',
          guidanceText: 'Claims often require certified death certificate and claim forms for each policy.',
        }),
        row({
          name: 'Health / auto / homeowners insurers',
          category: 'Property & casualty',
          guidanceText: 'Cancel or convert coverage to avoid lapse and unintended charges.',
        }),
      ];
    case 'accounts':
      return [
        row({
          name: 'Current employer — payroll & benefits',
          category: 'Employer',
          guidanceText: 'Final wages, PTO, COBRA, and retirement plan contacts are common follow-ups.',
        }),
        row({
          name: 'Former employers — pension / deferred comp',
          category: 'Employer',
          guidanceText: 'Older plans may require mailed forms; note any quarterly statement contacts.',
        }),
        row({
          name: 'Business partners / co-owners',
          category: 'Business',
          guidanceText: 'Operating agreements and buy-sell arrangements may drive notice timelines.',
        }),
        row({
          name: 'USPS mail forwarding',
          category: 'Mail',
          guidanceText: 'Temporary forwarding reduces missed statements and checks.',
        }),
        row({
          name: 'Recurring subscriptions (streaming, software, clubs)',
          category: 'Subscriptions',
          guidanceText: 'Cancel or transfer to avoid ongoing estate charges.',
        }),
        row({
          name: 'Social & digital accounts (memorialize / close)',
          category: 'Digital',
          guidanceText: 'Each platform has its own process and may require proof of death.',
        }),
        row({
          name: 'Utilities (power, water, gas, internet)',
          category: 'Utilities',
          guidanceText: 'Estate may need continued service during administration; plan final shutoff dates.',
        }),
      ];
    case 'employer':
      return [
        row({
          name: 'Current employer — payroll & benefits',
          category: 'Employer',
          guidanceText: 'Final wages, PTO, COBRA, and retirement plan contacts are common follow-ups.',
        }),
        row({
          name: 'Former employers — pension / deferred comp',
          category: 'Employer',
          guidanceText: 'Older plans may require mailed forms; note any quarterly statement contacts.',
        }),
        row({
          name: 'Business partners / co-owners',
          category: 'Business',
          guidanceText: 'Operating agreements and buy-sell arrangements may drive notice timelines.',
        }),
      ];
    case 'creditor':
      return [
        row({
          name: 'Newspaper / publication — creditor notice to publish',
          category: 'Publication',
          guidanceText: 'State probate rules set publication windows and proof deadlines for unknown creditors.',
        }),
        row({
          name: 'Known secured creditor (mortgage / auto / lienholder)',
          category: 'Secured',
          guidanceText: 'Coordinate payoff, assumption, or sale timelines with the lender.',
        }),
        row({
          name: 'Known unsecured creditor (credit card, personal loan)',
          category: 'Unsecured',
          guidanceText: 'Track claim deadlines after publication and direct notice.',
        }),
      ];
    case 'subscriptions':
      return [
        row({
          name: 'USPS mail forwarding',
          category: 'Mail',
          guidanceText: 'Temporary forwarding reduces missed statements and checks.',
        }),
        row({
          name: 'Recurring subscriptions (streaming, software, clubs)',
          category: 'Subscriptions',
          guidanceText: 'Cancel or transfer to avoid ongoing estate charges.',
        }),
        row({
          name: 'Social & digital accounts (memorialize / close)',
          category: 'Digital',
          guidanceText: 'Each platform has its own process and may require proof of death.',
        }),
        row({
          name: 'Utilities (power, water, gas, internet)',
          category: 'Utilities',
          guidanceText: 'Estate may need continued service during administration; plan final shutoff dates.',
        }),
      ];
    default:
      return [];
  }
}

export function withIds(rows: Omit<TrackedNotification, 'id'>[]): TrackedNotification[] {
  return rows.map((r) => ({ ...r, id: crypto.randomUUID() }));
}
